import * as React from "react";
import { useParams } from "react-router-dom";
import { NotFoundRoute } from "../NotFoundRoute.js";
import { TestLandingTemplate } from "./TestLandingTemplate.js";
import { TEST_LANDING_BY_SLUG } from "./configs.js";

export type { TestLandingConfig } from "./config.js";
export { TEST_LANDING_CONFIGS, TEST_LANDING_BY_SLUG } from "./configs.js";

/**
 * Top-level path segments owned by real (non-test) routes. The dynamic
 * `/:testSlug` route is registered LAST in `App.tsx`, so React Router's
 * specificity ranking already lets static routes win — but this set is a
 * belt-and-suspenders backstop: even if a test config were mistakenly given a
 * slug like "games", `TestLandingRoute` returns the shared 404 instead of
 * shadowing the real feature route.
 *
 * Keep in sync with the top-level segments in `App.tsx`.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  "games",
  "dashboard",
  "placement",
  "diagnostic",
  "feedback-call",
  "funding-check",
  "funding-brief",
  "welcome",
  "signin",
  "signup",
  "verify",
  "about",
  "privacy",
  "terms",
  "trust",
  "features",
  "compare",
  "plans",
  "practice",
  "quiz",
  "test-prep",
  "teachers",
  "teach",
  "join",
  "student",
  "family",
  "homework",
  "blog",
  "subscription",
  "admin",
  "play",
  "u",
  "a",
  "b",
  "c",
  "d",
  "e",
]);

/**
 * The single dynamic route backing every standardized-test landing page.
 * Reads `:testSlug`, looks it up in `TEST_LANDING_BY_SLUG`, and renders the
 * shared template on a hit (unless the slug is reserved) — otherwise falls
 * through to the shared 404.
 */
export function TestLandingRoute(): React.ReactElement {
  const { testSlug } = useParams<{ testSlug: string }>();

  if (!testSlug || RESERVED_SLUGS.has(testSlug)) {
    return <NotFoundRoute />;
  }

  const config = TEST_LANDING_BY_SLUG[testSlug];
  if (!config) {
    return <NotFoundRoute />;
  }

  return <TestLandingTemplate config={config} />;
}
